import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileText, Plus, Upload, Trash2, Image, Eye } from "lucide-react";
import type { ActivityEntry } from "@/domain/rdo/schema";
import { DialogFotos } from "@/components/rdo/DialogFotos";

interface ActivitiesModuleProps {
  value: ActivityEntry[];
  onChange: (v: ActivityEntry[]) => void;
  mode: "create" | "edit" | "view";
}

export function ActivitiesModule({ value, onChange, mode }: ActivitiesModuleProps) {
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState<number>(-1);
  const [selectedActivityFiles, setSelectedActivityFiles] = useState<FileList | null>(null);
  const [showActivityGallery, setShowActivityGallery] = useState(false);
  
  const isReadOnly = mode === "view";
  
  const addActivityRow = () => {
    const newActivity: ActivityEntry = {
      id: crypto.randomUUID(),
      title: "",
      location: "",
      progress: 0,
      responsible: "",
      notes: "",
      photos: []
    };
    onChange([...value, newActivity]);
  };

  const updateActivityRow = (index: number, field: keyof ActivityEntry, fieldValue: any) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: fieldValue };
    onChange(updated);
  };

  const removeActivityRow = (index: number) => {
    const updated = value.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handlePhotoUpload = (photos: Array<{ url: string; caption: string }>) => {
    if (selectedActivityIndex >= 0) {
      const updated = [...value];
      const newPhotos = photos.map(p => ({
        id: crypto.randomUUID(),
        url: p.url,
        caption: p.caption,
        hiddenFromReport: false,
        createdAt: new Date().toISOString()
      }));
      
      updated[selectedActivityIndex] = {
        ...updated[selectedActivityIndex],
        photos: [...(updated[selectedActivityIndex].photos || []), ...newPhotos]
      };
      
      onChange(updated);
    }
    setShowPhotoModal(false);
    setSelectedActivityIndex(-1);
    setSelectedActivityFiles(null);
  };

  const getActivityPhotos = (index: number) => {
    return value[index]?.photos || [];
  };

  const handleOpenActivityGallery = (index: number) => {
    setSelectedActivityIndex(index);
    setShowActivityGallery(true);
  };

  return (
    <Card data-section="activities">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Atividades ({value.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!isReadOnly ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs w-1/4">Atividade</TableHead>
                  <TableHead className="text-xs w-1/5">Local</TableHead>
                  <TableHead className="text-xs text-center w-20">% Concluída</TableHead>
                  <TableHead className="text-xs w-1/6">Responsável</TableHead>
                  <TableHead className="text-xs w-1/3">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {value.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Input
                        value={item.title}
                        onChange={(e) => updateActivityRow(index, 'title', e.target.value)}
                        placeholder="Ex: Concretagem da laje do 3º andar"
                        className="text-xs"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.location || ''}
                        onChange={(e) => updateActivityRow(index, 'location', e.target.value)}
                        placeholder="Ex: Bloco A - 3º andar"
                        className="text-xs"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={item.progress || 0}
                        onChange={(e) => updateActivityRow(index, 'progress', parseInt(e.target.value) || 0)}
                        min="0"
                        max="100"
                        className="text-xs text-center"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.responsible || ''}
                        onChange={(e) => updateActivityRow(index, 'responsible', e.target.value)}
                        placeholder="Ex: João Silva"
                        className="text-xs"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        {/* Upload button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const fileInput = document.createElement('input');
                            fileInput.type = 'file';
                            fileInput.accept = 'image/jpeg,image/jpg,image/png,image/heic';
                            fileInput.multiple = true;
                            fileInput.onchange = (e) => {
                              const files = (e.target as HTMLInputElement).files;
                              if (files && files.length > 0) {
                                setSelectedActivityIndex(index);
                                setSelectedActivityFiles(files);
                                setShowPhotoModal(true);
                              }
                            };
                            fileInput.click();
                          }}
                          aria-label="Enviar fotos da atividade"
                          className="h-8 w-8 p-0"
                        >
                          <Upload className="w-4 h-4" />
                        </Button>

                        {/* Delete activity button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeActivityRow(index)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>

                        {/* Photo thumbnails */}
                        {(() => {
                          const activityPhotos = getActivityPhotos(index);
                          const displayPhotos = activityPhotos.slice(0, 2);
                          const remainingCount = Math.max(0, activityPhotos.length - 2);
                          
                          return (
                            <>
                              {displayPhotos.map((photo: any, photoIndex: number) => (
                                <div
                                  key={photoIndex}
                                  className="w-8 h-8 border rounded cursor-pointer hover:scale-105 transition-transform overflow-hidden bg-muted"
                                  onClick={() => handleOpenActivityGallery(index)}
                                  title={photo.caption || 'Ver foto'}
                                >
                                  {photo.url ? (
                                    <img 
                                      src={photo.url} 
                                      alt={photo.caption} 
                                      className="w-full h-full object-cover" 
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Image className="w-3 h-3 text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                              ))}
                              
                              {remainingCount > 0 && (
                                <div
                                  className="w-8 h-8 border rounded cursor-pointer hover:scale-105 transition-transform bg-muted flex items-center justify-center text-xs font-medium"
                                  onClick={() => handleOpenActivityGallery(index)}
                                  title={`Ver mais ${remainingCount} foto${remainingCount !== 1 ? 's' : ''}`}
                                >
                                  +{remainingCount}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {value.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      Nenhuma atividade adicionada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            
            <Button
              variant="outline"
              onClick={addActivityRow}
              className="w-full mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Atividade
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Atividade</TableHead>
                  <TableHead className="text-xs">Local</TableHead>
                  <TableHead className="text-xs text-center">% Concluída</TableHead>
                  <TableHead className="text-xs">Responsável</TableHead>
                  <TableHead className="text-xs">Fotos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {value.map((activity, index) => (
                  <TableRow key={index}>
                    <TableCell className="text-xs">{activity.title}</TableCell>
                    <TableCell className="text-xs">{activity.location}</TableCell>
                    <TableCell className="text-xs text-center">
                      <Badge variant={activity.progress === 100 ? "default" : "secondary"}>
                        {activity.progress}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{activity.responsible}</TableCell>
                    <TableCell className="text-xs">
                      {activity.photos && activity.photos.length > 0 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenActivityGallery(index)}
                          className="h-8 w-8 p-0"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Photo Upload Modal - Simplified for now */}
        {showPhotoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg">
              <h3>Upload de Fotos</h3>
              <p>Funcionalidade em desenvolvimento</p>
              <Button onClick={() => setShowPhotoModal(false)}>Fechar</Button>
            </div>
          </div>
        )}

        {/* Photo Gallery Modal */}
        {showActivityGallery && selectedActivityIndex >= 0 && (
          <Dialog open={showActivityGallery} onOpenChange={setShowActivityGallery}>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>
                  Fotos - {value[selectedActivityIndex]?.title || "Atividade"}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {getActivityPhotos(selectedActivityIndex).map((photo: any, photoIndex: number) => (
                  <div key={photoIndex} className="relative">
                    <img 
                      src={photo.url} 
                      alt={photo.caption} 
                      className="w-full h-32 object-cover rounded border" 
                    />
                    {photo.caption && (
                      <p className="text-xs mt-1 text-muted-foreground">{photo.caption}</p>
                    )}
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}